/* ============================================================
   TOUCHSCREEN — the remote's lower LCD panel. Replaces the old
   0-9 numpad with a little glass screen that switches between two
   views: the channel DIAL (the digits) and an OPTIONS menu of
   display toggles. All state is in the store; this only renders it.
   ============================================================ */
import { useTv } from '@/tv/context'
import { SESSION_ID } from '@/session'
import type { DisplaySettingKey, QueueItem } from '@/tv/types'
import styles from './Touchscreen.module.css'
import { createPortal } from 'react-dom'

const OPTIONS: Array<{ key: DisplaySettingKey; label: string; hint: string }> = [
  { key: 'bareVideo', label: 'Bare video', hint: 'strip everything but the picture' },
  { key: 'crt', label: 'CRT glass', hint: 'all tube overlays' },
  { key: 'scanlines', label: 'Scanlines', hint: 'moving scan overlay' },
  { key: 'curvature', label: 'Curved glass', hint: 'bulge · vignette · glare' },
  { key: 'ambient', label: 'Room glow', hint: 'ambient light · dust' },
  { key: 'adBumpers', label: 'Ad bumpers', hint: 'full-screen ad breaks between programs' },
  { key: 'overscan', label: 'Overscan', hint: 'let the bezel crop the picture edges' },
  { key: 'trueFullscreen', label: 'True fullscreen', hint: 'stretch the video edge-to-edge' },
]

const pad = (n: number) => (n < 10 ? '0' : '') + n

/** The scrollable list of the current channel's videos in play order. Ad slots
    in the queue are hidden here (they show in the TV guide as "AD BREAK"); each
    row keeps its ORIGINAL queue index so playProgram/programIndex stay aligned. */
function VideoList() {
  const tv = useTv()
  const { queue, programIndex, shuffle } = tv.state
  const rows = queue
    .map((q, i) => ({ q, i }))
    .filter((r): r is { q: Extract<QueueItem, { kind: 'video' }>; i: number } => r.q.kind === 'video')

  return (
    <div className={styles.list}>
      <div className={styles.listBar}>
        <span className={styles.listCount}>{rows.length} videos</span>
        <button
          className={[styles.shuffle, shuffle && styles.shuffleOn].filter(Boolean).join(' ')}
          onClick={tv.shuffleToggle}
        >
          ⤮ Shuffle{shuffle ? ' ✓' : ''}
        </button>
      </div>
      <div className={styles.scroll}>
        {rows.length === 0 ? (
          <div className={styles.empty}>No videos on this channel</div>
        ) : (
          rows.map(({ q, i }, n) => (
            <button
              key={q.videoId + i}
              className={[styles.listRow, i === programIndex && styles.playing].filter(Boolean).join(' ')}
              onClick={() => tv.playProgram(i)}
            >
              <span className={styles.listNum}>{pad(n + 1)}</span>
              <span className={styles.listTitle}>{q.title || q.videoId}</span>
              {i === programIndex ? <span className={styles.listNow}>▶</span> : null}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

export function Touchscreen() {
  const tv = useTv()
  const view = tv.state.touchScreen
  const reported = tv.state.bugReport
  const s = tv.state.settings
  const adConfirm = tv.state.adBumperConfirm

  return (
    // data-no-drag: the panel scrolls + has its own buttons, so it must never start
    // a remote drag (see startRemoteDrag's interactive-target guard).
    <div className={styles.touch} data-no-drag>
      <div className={styles.head}>
        <button
          className={[styles.tab, view === 'dial' && styles.tabOn].filter(Boolean).join(' ')}
          onClick={() => tv.setTouchScreen('dial')}
        >
          Dial
        </button>
        <button
          className={[styles.tab, view === 'list' && styles.tabOn].filter(Boolean).join(' ')}
          onClick={() => tv.setTouchScreen('list')}
        >
          ☰ List
        </button>
        <button
          className={[styles.tab, view === 'options' && styles.tabOn].filter(Boolean).join(' ')}
          onClick={() => tv.setTouchScreen('options')}
        >
          ⚙
        </button>
      </div>

      {/* Fixed-height body so switching views never resizes the remote; content scrolls. */}
      <div className={styles.body}>
      {view === 'list' ? (
        <VideoList />
      ) : view === 'dial' ? (
        <div className={styles.dial}>
          <div className={styles.pad}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
              <button key={d} className={styles.key} data-digit={d} onClick={() => tv.act('digit', d)}>
                {d}
              </button>
            ))}
            <span className={styles.spacer} />
            <button className={styles.key} data-digit={0} onClick={() => tv.act('digit', 0)}>
              0
            </button>
            <span className={styles.spacer} />
          </div>
          <button
            className={[styles.srt, tv.state.subtitles && styles.srtOn].filter(Boolean).join(' ')}
            aria-pressed={tv.state.subtitles}
            onClick={tv.toggleSubtitles}
          >
            SRT
          </button>
        </div>
      ) : (
        <div className={styles.options}>
          {OPTIONS.map((o) => {
            // bareVideo overrides the CRT-layer toggles; adBumpers + trueFullscreen
            // are orthogonal (scheduling / fullscreen sizing), so they stay live.
            const overridden = s.bareVideo && o.key !== 'bareVideo' && o.key !== 'adBumpers' && o.key !== 'trueFullscreen'
            const on = o.key === 'bareVideo' ? s.bareVideo : !overridden && s[o.key]
            return (
              <button
                key={o.key}
                className={[styles.option, overridden && styles.dim].filter(Boolean).join(' ')}
                onClick={(e) => {
                  // Hand the actual click point to the store so the ad-confirm
                  // button can spawn clear of the pointer (it flees on approach).
                  tv.toggleSetting(o.key, { left: e.clientX, top: e.clientY })
                }}
              >
                <span className={styles.optionLabel}>{o.label}</span>
                <span className={styles.optionHint}>{o.hint}</span>
                <span className={[styles.optionLed, on && styles.optionLedOn].filter(Boolean).join(' ')} />
              </button>
            )
          })}
          <div className={[styles.option, styles.sizeRow].join(' ')}>
            <span className={styles.optionLabel}>Remote size</span>
            <span className={styles.optionHint}>scale the remote to fit your screen</span>
            <span className={styles.sizeStepper}>
              <button className={styles.sizeBtn} onClick={() => tv.setRemoteScale(-1)} aria-label="Smaller">−</button>
              <span className={styles.sizeVal}>{Math.round(tv.state.remoteScale * 100)}%</span>
              <button className={styles.sizeBtn} onClick={() => tv.setRemoteScale(1)} aria-label="Bigger">+</button>
            </span>
          </div>
          <button
            className={[styles.option, styles.reportBug, reported && styles.reportBugDone].filter(Boolean).join(' ')}
            onClick={tv.reportBug}
          >
            <span className={styles.optionLabel}>
              {reported === 'copied' ? `Reported · ${SESSION_ID}` : reported === 'nocopy' ? `Reported · ${SESSION_ID}` : 'Report bug'}
            </span>
            <span className={styles.optionHint}>
              {reported === 'copied' ? 'ID copied to clipboard' : reported === 'nocopy' ? 'copy ID manually and hand to agent' : 'send current session state to Sentry'}
            </span>
          </button>
          {adConfirm && typeof document !== 'undefined' ? createPortal(
            <button
              className={styles.adConfirm}
              style={{ left: adConfirm.left, top: adConfirm.top }}
              onClick={tv.confirmAdBumpersOff}
            >
              <span>{adConfirm.label}</span>
            </button>,
            document.body,
          ) : null}
        </div>
      )}
      </div>
    </div>
  )
}
